// src/components/notifications/Bell.jsx
import React, { useEffect, useRef, useState } from "react";
import { Bell as BellIcon } from "lucide-react";
import { fetchUnreadCount, notifBus, calcNewCount } from "../../constants/notifications";
import { useNotificationSocket } from "../../hooks/useSocket";
import AppButton from "../ui/AppButton.jsx";
import { cn } from "../../utils/themeClasses.js";
export default function Bell({
  onClick,
  open = false,
  controlsId
}) {
  const [count, setCount] = useState(0);
  const timerRef = useRef(null);
  const aliveRef = useRef(true);
  const loadingRef = useRef(false);
  const load = async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    try {
      const n = await fetchUnreadCount();
      if (aliveRef.current) setCount(Number.isFinite(n) ? n : 0);
    } catch {
      if (aliveRef.current) setCount(v => v ?? 0);
    } finally {
      loadingRef.current = false;
    }
  };

  const loadRef = useRef(load);
  loadRef.current = load;

  // 🔌 Echtzeit: Badge immer vom Server (`/notifications/unread_count`), kein blindes +1
  useNotificationSocket(() => {
    void loadRef.current?.();
  });
  useEffect(() => {
    aliveRef.current = true;
    const run = () => {
      if (document.visibilityState === "visible") load();
    };

    // initial + Intervall (jetzt seltener da WebSocket)
    run();
    timerRef.current = window.setInterval(run, 60_000); // Reduziert auf 60s als Fallback

    // Sichtbarkeit
    const onVis = () => run();
    document.addEventListener("visibilitychange", onVis);

    // auf Events aus der Liste reagieren (read-one/read-all)
    const off = notifBus.on?.("changed", ev => {
      const {
        kind
      } = ev.detail || {};
      if (kind === "read-all") {
        setCount(0);
      } else if (kind === "read-one") {
        setCount(old => calcNewCount(old, "read-one", 1));
      } else if (kind === "mark-related") {
        const u = Number(ev.detail?.updated || 0);
        setCount(old => calcNewCount(old, "mark-related", u || 0));
      }
      // Sicherheitshalber kurz danach „echt“ nachladen
      window.setTimeout(() => aliveRef.current && load(), 400);
    });
    return () => {
      aliveRef.current = false;
      if (timerRef.current) clearInterval(timerRef.current);
      document.removeEventListener("visibilitychange", onVis);
      // kompatibel zu EventEmitter / EventTarget
      if (typeof off === "function") off();
      // falls Listeners via addEventListener hinzugefügt wurden
      try {
        notifBus?.removeEventListener?.("changed", onVis);
      } catch {}
    };
  }, []);
    const badge = count > 99 ? "99+" : count;
  return <AppButton type="button" variant="ghost" onClick={e => {
    e.stopPropagation();
    onClick?.();
  }} className={cn("relative inline-flex items-center justify-center h-10 w-10 rounded-xl min-h-[44px] min-w-[44px]", open ? "icon-btn-active" : "icon-btn-surface")} title="Benachrichtigungen" aria-haspopup="dialog" aria-expanded={open} aria-controls={controlsId} aria-label="Benachrichtigungen öffnen" data-notif-anchor>
      <BellIcon size={18} className={""} />
      {count > 0 && <span className="absolute -top-1 -right-1 min-w-[20px] h-[20px] px-1 grid place-items-center
             rounded-full bg-rose-600 text-white text-[10px] border border-white/30">
          {badge}
        </span>}

      {/* SR-only Live-Region */}
      <span className="sr-only" aria-live="polite">
        {count > 0 ? `${count} neue Benachrichtigungen` : "Keine neuen Benachrichtigungen"}
      </span>
    </AppButton>;
}